import React, { useMemo } from "react";
import "./AllProductsHero.css";

import heroImg from "../../../../assets/images/allproducts/allphero.png";
import { resolveMediaUrl } from "../../../../utils/media";
import { usePageContentAliases } from "../../../../hooks/usePublicQueries";
import { useTranslation } from "react-i18next";

const fallbackAllProductsHero = {
  tag: "ALL PRODUCTS",
  titleLine1: "High quality products",
  titleLine2: "for every need",
  description:
    "IRQGYP is a leading Iraqi company specialized in gypsum products and modern construction solutions — serving local and regional markets with a strong commitment to quality, sustainability, and long-term partnerships.",
  linkText: "Explore all products",
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceHeroPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") return tryParseJsonObject(raw) || null;
  if (typeof raw !== "object") return null;
  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const findPageSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const PRODUCTS_PAGE_ALIASES = ["allproducts", "all-products", "products", "allproduct"];

const AllProductsHero = ({ content }) => {
  const { i18n } = useTranslation();
  const shouldFetch = !(content && typeof content === "object");
  const { data: pageContent } = usePageContentAliases(PRODUCTS_PAGE_ALIASES, i18n.language, {
    enabled: shouldFetch,
  });

  const apiContent = useMemo(() => {
    if (!shouldFetch) return null;
    return (
      findPageSection(pageContent, "hero") ||
      findPageSection(pageContent, "allproductshero") ||
      findPageSection(pageContent, "allproducts") ||
      null
    );
  }, [pageContent, shouldFetch]);

  const view = useMemo(() => {
    const source = content ?? apiContent;
    const merged = coalesceHeroPayload(source);
    const section = hasSectionData(merged) ? merged : fallbackAllProductsHero;
    const singleTitle = pickString(section.title);
    let titleLine1 = pickString(section.titleLine1, section.heading);
    let titleLine2 = pickString(section.titleLine2, section.subheading);
    if (!titleLine1 && singleTitle) {
      if (singleTitle.includes("\n")) {
        const parts = singleTitle.split("\n").map((p) => p.trim());
        titleLine1 = parts[0] || "";
        titleLine2 = parts.slice(1).join(" ").trim() || titleLine2;
      } else {
        titleLine1 = singleTitle;
      }
    }

    return {
      tag: pickString(section.tag, section.sectionTag) || fallbackAllProductsHero.tag,
      titleLine1: titleLine1 || fallbackAllProductsHero.titleLine1,
      titleLine2:
        titleLine2 || (singleTitle && !singleTitle.includes("\n") ? "" : fallbackAllProductsHero.titleLine2),
      description:
        pickString(section.description, section.body, section.text, section.summary, section.content) ||
        fallbackAllProductsHero.description,
      linkText: pickString(section.linkText, section.exploreText, section.ctaText) || fallbackAllProductsHero.linkText,
      imageSrc:
        resolveMediaUrl(section.image || section.rightImage || section.heroImage || section.mediaUrl || section.photo) ||
        heroImg,
    };
  }, [content]);

  return (
    <section className="allProductsHero">
      <div className="allProductsHero-container">
        <div className="allProductsHero-left">
          <span className="allProductsHero-tag">{view.tag}</span>

          <h1 className="allProductsHero-title">
            {view.titleLine1}
            {view.titleLine2 ? (
              <>
                <br />
                {view.titleLine2}
              </>
            ) : null}
          </h1>

          <p className="allProductsHero-desc">{view.description}</p>

          <div className="allProductsHero-link">
            {view.linkText}
            <span className="arrow">↓</span>
          </div>
        </div>

        <div className="allProductsHero-right">
          <img
            src={view.imageSrc}
            alt=""
            draggable={false}
            onError={(e) => {
              if (e.currentTarget.src !== heroImg) {
                e.currentTarget.src = heroImg;
              }
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default AllProductsHero;
